import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './App.css';
import { Monitor, Layout, Map, Power, Info } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import LoginButton from './LoginButton';
import LogoutButton from './LogoutButton';
import TemplateList from './components/TemplateList';
import TemplateEditor from './components/TemplateEditor';
import DeviceManager from './components/DeviceManager';
import FloorPlan from './components/FloorPlan';
import PairView from './components/PairView';

const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
const SOCKET_URL = API_BASE_URL || undefined;

const DEV_USER = { sub: 'dev|local', name: 'Dev User', email: 'dev@localhost' };

function App({ devMode = false }) {
  // In devMode (no Auth0 creds configured) we mock the auth state so every
  // part of the UI is reachable without signing in.
  const auth0 = devMode
    ? { isAuthenticated: true, isLoading: false, error: null, user: DEV_USER }
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useAuth0(); // only called when Auth0Provider is in the tree

  const { isAuthenticated, isLoading, error, user } = auth0;

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [displayStatus, setDisplayStatus] = useState('Unknown');
  const [tvStatus, setTvStatus] = useState('Unknown');
  const [message, setMessage] = useState('');
  const [debugMessages, setDebugMessages] = useState([]);

  const [tab, setTab] = useState('templates');
  const [editing, setEditing] = useState(undefined); // undefined = list, null = new, object = edit

  // Pairing deep-link: /pair?token=... or a stashed token after Auth0 round-trip
  const [showPair, setShowPair] = useState(
    window.location.pathname === '/pair' || !!localStorage.getItem('pendingPairToken')
  );

  useEffect(() => {
    let newSocket;
    try {
      newSocket = io(SOCKET_URL || undefined, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 45000,
        autoConnect: true,
      });
    } catch (err) {
      newSocket = io(SOCKET_URL || 'http://localhost:3001');
    }

    newSocket.on('connect', () => {
      setConnected(true);
      setMessage('Connected to server');
      const identifyData = { clientType: 'frontend' };
      if (isAuthenticated && user) {
        identifyData.userId = user.sub;
        identifyData.userEmail = user.email;
      }
      newSocket.emit('identify', identifyData);
    });

    newSocket.on('disconnect', () => { setConnected(false); setMessage('Disconnected from server'); });
    newSocket.on('connect_error', (e) => setMessage(`Connection error: ${e.message}`));
    newSocket.on('display_status', (s) => setDisplayStatus(s));
    newSocket.on('display_status_update', (d) => d && setDisplayStatus(d.status || 'Unknown'));
    newSocket.on('tv_status', (s) => setTvStatus(s));
    newSocket.on('tv_status_update', (d) => d && setTvStatus(d.status || 'Unknown'));
    newSocket.on('message', (m) => setMessage(m));
    newSocket.on('debug_message', (d) => setDebugMessages((prev) => [d, ...prev].slice(0, 50)));

    setSocket(newSocket);
    return () => newSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-identify with user info once authenticated
  useEffect(() => {
    if (socket && connected && isAuthenticated && user) {
      socket.emit('identify', { clientType: 'frontend', userId: user.sub, userEmail: user.email });
    }
  }, [socket, connected, isAuthenticated, user]);

  const sendTvCommand = (command, params = {}) => {
    if (socket && connected) {
      const data = { command, params };
      if (isAuthenticated && user) data.userId = user.sub;
      socket.emit('tv_command', data);
      setMessage(`Sent TV command: ${command}`);
    } else {
      setMessage('Not connected to server');
    }
  };

  const exitPair = () => {
    localStorage.removeItem('pendingPairToken');
    window.history.replaceState({}, '', '/');
    setShowPair(false);
  };

  // ---- conditional rendering (all hooks above are always called) ----
  if (showPair) return <PairView onDone={exitPair} />;

  if (isLoading) {
    return <div className="app-container"><div className="loading-state"><div className="loading-text">Loading…</div></div></div>;
  }

  if (error) {
    return (
      <div className="app-container"><div className="error-state">
        <div className="error-title">Oops!</div>
        <div className="error-message">Something went wrong</div>
        <div className="error-sub-message">{error.message}</div>
      </div></div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app-container"><div className="main-card-wrapper">
        <h1 className="main-title">Template Engine</h1>
        <div className="action-card">
          <p className="action-text">Please sign in to access the template builder</p>
          <LoginButton />
        </div>
      </div></div>
    );
  }

  const userId = user?.sub;
  const TABS = [
    { key: 'templates', label: 'Templates', icon: Layout },
    { key: 'devices', label: 'Devices', icon: Monitor },
    { key: 'floorplan', label: 'Floor Plan', icon: Map },
    { key: 'control', label: 'TV Control', icon: Power },
  ];

  return (
    <div className="container">
      <header className="app-header">
        <h1 className="app-title">Template Engine</h1>
        <p className="app-subtitle">Create and manage display templates</p>

        <div className="auth-profile-section">
          <div className="profile-compact">
            {user && <span style={{ color: '#fff', marginRight: 15 }}>{user.name}{devMode && ' (dev)'}</span>}
            {!devMode && <LogoutButton />}
          </div>
        </div>

        <div className="status-bar">
          <div className="status-item">
            <span className={`status-indicator ${connected ? 'status-connected' : 'status-disconnected'}`}></span>
            <span>{connected ? 'Connected' : 'Not connected'}</span>
          </div>
          <div className="status-item">
            <span className={`status-indicator ${displayStatus === 'Connected' ? 'status-connected' : 'status-unknown'}`}></span>
            <span>Display: {displayStatus}</span>
          </div>
          <div className="status-item">
            <span className={`status-indicator ${tvStatus === 'Connected' ? 'status-connected' : 'status-unknown'}`}></span>
            <span>TV: {tvStatus}</span>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, margin: '0 0 20px', flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setEditing(undefined); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontWeight: 600,
                background: isActive ? '#2563eb' : '#1f2a3d', color: '#fff' }}>
              <Icon size={18} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="card-container">
        {tab === 'templates' && (
          editing === undefined
            ? <TemplateList userId={userId} onEdit={setEditing} />
            : <TemplateEditor template={editing} userId={userId}
                onSaved={() => setEditing(undefined)} onCancel={() => setEditing(undefined)} />
        )}
        {tab === 'devices' && <DeviceManager socket={socket} userId={userId} />}
        {tab === 'floorplan' && <FloorPlan socket={socket} userId={userId} />}
        {tab === 'control' && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', padding: '1rem' }}>
            <button className="button power-on" onClick={() => sendTvCommand('power_on')}>Power On</button>
            <button className="button power-off" onClick={() => sendTvCommand('power_off')}>Power Off</button>
          </div>
        )}
      </div>

      {debugMessages.length > 0 && (
        <div className="card-container">
          <h2 className="card-title"><Info size={24} /> Debug Log</h2>
          <div style={{ maxHeight: 240, overflowY: 'auto', padding: '1rem', background: '#f8fafc', borderRadius: 8 }}>
            {debugMessages.map((m, i) => (
              <div key={i} style={{ fontSize: '0.8rem', marginBottom: '0.5rem', fontFamily: 'monospace' }}>
                <span style={{ color: '#64748b' }}>{m.timestamp}</span> {m.message}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="message-bar"><p className="message-text">{message}</p></div>
    </div>
  );
}

export default App;
